import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import {
  getGroups,
  getGroup,
  createGroup,
  updateGroup,
  deleteGroup,
  addGroupMember,
  removeGroupMember,
  getMembers,
} from '../api';

function memberLabel(m) {
  if (!m) return '';
  const first = m.first_name ?? '';
  const last = m.last_name ?? '';
  return [first, last].filter(Boolean).join(' ').trim() || m.email || `Member #${m.id}`;
}

export default function GroupsPage() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [dialog, setDialog] = useState({ open: false, mode: 'add', item: null, name: '' });
  const [membersDialog, setMembersDialog] = useState({ open: false, group: null, members: [], allMembers: [] });
  const [memberSearch, setMemberSearch] = useState('');
  const [membersLoading, setMembersLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const load = async () => {
    try {
      const data = await getGroups();
      setGroups(data);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleOpenAdd = () => {
    setDialog({ open: true, mode: 'add', item: null, name: '' });
  };

  const handleOpenEdit = (g) => {
    setDialog({ open: true, mode: 'edit', item: g, name: g.name });
  };

  const handleCloseDialog = () => setDialog((d) => ({ ...d, open: false }));

  const handleSave = async () => {
    try {
      if (dialog.mode === 'add') {
        await createGroup({ name: dialog.name.trim() });
        setSnackbar({ open: true, message: 'Group created', severity: 'success' });
      } else {
        await updateGroup(dialog.item.id, { name: dialog.name.trim() });
        setSnackbar({ open: true, message: 'Group updated', severity: 'success' });
      }
      handleCloseDialog();
      load();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleOpenMembers = async (group) => {
    setMembersLoading(true);
    try {
      const [groupData, membersRes] = await Promise.all([
        getGroup(group.id),
        getMembers({ pageSize: 999, sortBy: 'last_name', sortOrder: 'asc' }),
      ]);
      const allMembers = Array.isArray(membersRes) ? membersRes : (membersRes?.data ?? []);
      setMembersDialog({
        open: true,
        group: groupData,
        members: groupData?.members ?? [],
        allMembers,
      });
      setMemberSearch('');
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setMembersLoading(false);
    }
  };

  const handleCloseMembersDialog = () => {
    setMembersDialog({ open: false, group: null, members: [], allMembers: [] });
    load();
  };

  const handleAddMember = async (member) => {
    if (!member || !membersDialog.group) return;
    const alreadyIn = membersDialog.members.some((m) => m.id === member.id);
    if (alreadyIn) return;
    try {
      const updated = await addGroupMember(membersDialog.group.id, member.id);
      setMembersDialog((d) => ({ ...d, members: updated?.members ?? [...d.members, member] }));
      setMemberSearch('');
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleRemoveMember = async (member) => {
    if (!membersDialog.group) return;
    try {
      const updated = await removeGroupMember(membersDialog.group.id, member.id);
      setMembersDialog((d) => ({
        ...d,
        members: updated?.members ?? d.members.filter((m) => m.id !== member.id),
      }));
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteGroup(deleteConfirm.id);
      setSnackbar({ open: true, message: 'Group deleted', severity: 'success' });
      setDeleteConfirm(null);
      load();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const memberOptions = (membersDialog.allMembers || []).filter((m) => {
    const inGroup = (membersDialog.members || []).some((gm) => gm.id === m.id);
    if (inGroup) return false;
    if (!memberSearch.trim()) return true;
    const label = memberLabel(m).toLowerCase();
    const search = memberSearch.toLowerCase();
    return label.includes(search);
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Groups</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>
          Add Group
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Members</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3}>Loading...</TableCell>
              </TableRow>
            ) : (
              groups.map((g) => (
                <TableRow key={g.id}>
                  <TableCell>{g.name}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      onClick={() => handleOpenMembers(g)}
                      sx={{ textTransform: 'none' }}
                    >
                      {(g.members || []).length} member{(g.members || []).length !== 1 ? 's' : ''}
                    </Button>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpenMembers(g)} title="Manage members">
                      <PeopleIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleOpenEdit(g)} title="Edit group name">
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteConfirm(g)}
                      title="Delete group"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add / Edit group name dialog */}
      <Dialog open={dialog.open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{dialog.mode === 'add' ? 'Add Group' : 'Edit Group'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Name"
            value={dialog.name}
            onChange={(e) => setDialog({ ...dialog, name: e.target.value })}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!dialog.name.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage members dialog */}
      <Dialog
        open={membersDialog.open}
        onClose={handleCloseMembersDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {membersDialog.group?.name} – Members
        </DialogTitle>
        <DialogContent>
          <Autocomplete
            options={memberOptions}
            getOptionLabel={memberLabel}
            inputValue={memberSearch}
            onInputChange={(_, v) => setMemberSearch(v)}
            onChange={(_, value) => value && handleAddMember(value)}
            loading={membersLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Add member"
                placeholder="Search by name..."
                size="small"
                sx={{ mb: 2 }}
              />
            )}
          />
          <List dense>
            {(membersDialog.members || []).map((m) => (
              <ListItem key={m.id}>
                <ListItemText primary={memberLabel(m)} />
                <ListItemSecondaryAction>
                  <IconButton
                    size="small"
                    edge="end"
                    onClick={() => handleRemoveMember(m)}
                    title="Remove from group"
                  >
                    <PersonRemoveIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
            {(membersDialog.members || []).length === 0 && (
              <ListItem>
                <ListItemText primary="No members in this group" secondary="Use the search above to add members" />
              </ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMembersDialog}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Group</DialogTitle>
        <DialogContent>
          Are you sure you want to delete &quot;{deleteConfirm?.name}&quot;?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
